import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import TodaySession from '../components/TodaySession';
import axios from 'axios';
import { 
  Music, 
  Dumbbell, 
  BookOpen, 
  FileText, 
  Mic2, 
  Drum,
  Crown,
  TrendingUp,
  Target,
  Circle,
  CheckCircle,
  Flame,
  Lock,
  BarChart3,
  Calendar,
  Timer
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const { t } = useTranslation();
  const { user, isPremium, isAuthenticated } = useAuth();
  const [stats, setStats] = useState(null);
  const [progressStats, setProgressStats] = useState(null);
  const [advancedStats, setAdvancedStats] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/stats`);
        setStats(response.data);
      } catch (error) {
        console.error('Error fetching stats:', error);
      }
    };
    fetchStats();
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      const fetchProgressStats = async () => {
        try {
          const response = await axios.get(`${API}/progress/stats`);
          setProgressStats(response.data);
        } catch (error) {
          console.error('Error fetching progress stats:', error);
        }
      };
      fetchProgressStats();
    }
  }, [isAuthenticated]);

  // Fetch advanced stats only for PRO users
  useEffect(() => {
    if (isAuthenticated && isPremium) {
      const fetchAdvancedStats = async () => {
        try {
          const response = await axios.get(`${API}/progress/advanced`);
          setAdvancedStats(response.data);
        } catch (error) {
          console.error('Error fetching advanced stats:', error);
        }
      };
      fetchAdvancedStats();
    }
  }, [isAuthenticated, isPremium]);

  const totalCompleted = progressStats 
    ? (progressStats.exercises?.completed || 0) + 
      (progressStats.lessons?.completed || 0) + 
      (progressStats.songs?.completed || 0)
    : 0;

  const quickLinks = [
    { path: '/chords', icon: Music, label: t('nav.chords'), color: 'from-amber to-orange-500', statKey: 'chords', statLabel: t('dashboard.units.chords') },
    { path: '/exercises', icon: Dumbbell, label: t('nav.exercises'), color: 'from-blue to-cyan-400', statKey: 'exercises', statLabel: t('dashboard.units.exercises') },
    { path: '/theory', icon: BookOpen, label: t('nav.theory'), color: 'from-purple-500 to-pink-500', statKey: 'lessons', statLabel: t('dashboard.units.lessons') },
    { path: '/songs', icon: FileText, label: t('nav.songs'), color: 'from-green-500 to-emerald-400', statKey: 'songs', statLabel: t('dashboard.units.songs') },
    { path: '/tuner', icon: Mic2, label: t('nav.tuner'), color: 'from-red-500 to-rose-400' },
    { path: '/drums', icon: Drum, label: t('nav.drums'), color: 'from-yellow-500 to-amber', statKey: 'drum_tracks', statLabel: t('dashboard.units.tracks') },
    { path: '/metronome', icon: Timer, label: t('nav.metronome'), color: 'from-indigo-500 to-purple-500', description: t('dashboard.metronomeDesc') },
    { path: '/recorder', icon: Circle, label: t('nav.recorder'), color: 'from-rose-500 to-red-400', description: t('dashboard.recorderDesc') },
  ];

  const levelColors = {
    beginner: 'from-green-500 to-emerald-400',
    intermediate: 'from-blue to-cyan-400',
    expert: 'from-purple-500 to-pink-500'
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-20 pb-12 px-6 md:px-12" data-testid="dashboard-page">
      <div className="max-w-7xl mx-auto">
        {/* Welcome Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-2">
            {t('dashboard.welcome')}, {user?.username}!
          </h1>
          <p className="text-zinc-400 text-lg">
            {t('dashboard.continueLeaning')}
          </p>
        </motion.div>

        {/* Bento Grid Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Today's Session widget (replaces former "Continue Learning" static hero) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="lg:col-span-2 lg:row-span-2"
          >
            <TodaySession lang={i18n.language?.startsWith('it') ? 'it' : 'en'} />
          </motion.div>

          {/* Level Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-[#121212] border border-white/5 rounded-2xl p-6 hover:border-amber/30 transition-colors"
          >
            <div className="flex items-center justify-between mb-4">
              <span className="text-zinc-400 text-sm uppercase tracking-wide">{t('dashboard.yourLevel')}</span>
              <TrendingUp className="w-4 h-4 text-amber" />
            </div>
            <div className={`inline-block px-4 py-2 rounded-full bg-gradient-to-r ${levelColors[user?.level || 'beginner']} text-black font-bold`}>
              {t(`levels.${user?.level || 'beginner'}`)}
            </div>
          </motion.div>

          {/* Premium Status */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className={`bg-[#121212] border rounded-2xl p-6 transition-colors ${isPremium ? 'border-amber/30 animate-pulse-glow' : 'border-white/5 hover:border-amber/30'}`}
          >
            <div className="flex items-center gap-3 mb-4">
              <Crown className={`w-6 h-6 ${isPremium ? 'text-amber' : 'text-zinc-500'}`} />
              <span className="text-zinc-400 text-sm uppercase tracking-wide">{t('dashboard.status')}</span>
            </div>
            {isPremium ? (
              <span className="text-amber font-bold text-lg">{t('common.premium')}</span>
            ) : (
              <Link to="/premium">
                <Button
                  size="sm"
                  data-testid="upgrade-premium-btn"
                  className="bg-gradient-to-r from-amber to-orange-500 text-black font-semibold"
                >
                  {t('dashboard.upgrade')}
                </Button>
              </Link>
            )}
          </motion.div>

          {/* Progress Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="bg-[#121212] border border-white/5 rounded-2xl p-6 hover:border-green-500/30 transition-colors"
          >
            <div className="flex items-center gap-3 mb-4">
              <CheckCircle className="w-6 h-6 text-green-500" />
              <span className="text-zinc-400 text-sm uppercase tracking-wide">{t('dashboard.yourProgress')}</span>
            </div>
            {progressStats ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-zinc-400 text-sm flex items-center gap-2">
                    <Dumbbell className="w-4 h-4" /> {t('nav.exercises')}
                  </span>
                  <span className="text-green-400 font-bold">{progressStats.exercises?.completed || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-zinc-400 text-sm flex items-center gap-2">
                    <BookOpen className="w-4 h-4" /> {t('nav.theory')}
                  </span>
                  <span className="text-green-400 font-bold">{progressStats.lessons?.completed || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-zinc-400 text-sm flex items-center gap-2">
                    <Music className="w-4 h-4" /> {t('nav.songs')}
                  </span>
                  <span className="text-green-400 font-bold">{progressStats.songs?.completed || 0}</span>
                </div>
                <div className="pt-2 border-t border-white/10">
                  <div className="flex items-center justify-between">
                    <span className="text-white font-medium">{t('dashboard.total')}</span>
                    <span className="text-green-400 font-bold text-lg">{totalCompleted}</span>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-zinc-500 text-sm">{t('common.loading')}</p>
            )}
          </motion.div>

          {/* Advanced Stats - PRO Only */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.38 }}
            className={`bg-[#121212] border rounded-2xl p-6 transition-colors ${
              isPremium ? 'border-purple-500/30' : 'border-white/5'
            }`}
          >
            <div className="flex items-center gap-3 mb-4">
              <BarChart3 className={`w-6 h-6 ${isPremium ? 'text-purple-400' : 'text-zinc-500'}`} />
              <span className="text-zinc-400 text-sm uppercase tracking-wide">{t('dashboard.advancedStats')}</span>
              {!isPremium && <Crown className="w-4 h-4 text-amber ml-auto" />}
            </div>

            {isPremium ? (
              advancedStats ? (
                <div className="space-y-4">
                  {/* Summary */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-white/5 rounded-lg p-3">
                      <p className="text-zinc-500 text-xs">{t('dashboard.activeDays')}</p>
                      <p className="text-purple-400 font-bold text-xl">{advancedStats.summary?.active_days || 0}</p>
                    </div>
                    <div className="bg-white/5 rounded-lg p-3">
                      <p className="text-zinc-500 text-xs">{t('dashboard.totalTime')}</p>
                      <p className="text-purple-400 font-bold text-xl">{advancedStats.summary?.total_time_minutes || 0}m</p>
                    </div>
                  </div>

                  {/* Daily Activity (Last 7 days) */}
                  {advancedStats.daily_counts_last_30_days?.length > 0 && (
                    <div>
                      <p className="text-zinc-500 text-xs mb-2 flex items-center gap-1">
                        <Calendar className="w-3 h-3" /> {t('dashboard.last7Days')}
                      </p>
                      <div className="flex gap-1">
                        {advancedStats.daily_counts_last_30_days.slice(0, 7).map((day, i) => (
                          <div
                            key={i}
                            className="flex-1 bg-purple-500/20 rounded text-center py-1"
                            title={`${day.date}: ${day.count}`}
                          >
                            <span className="text-purple-400 text-xs font-bold">{day.count}</span>
                          </div>
                        ))}
                        {advancedStats.daily_counts_last_30_days.length < 7 &&
                          [...Array(7 - advancedStats.daily_counts_last_30_days.length)].map((_, i) => (
                            <div key={`empty-${i}`} className="flex-1 bg-white/5 rounded text-center py-1">
                              <span className="text-zinc-600 text-xs">-</span>
                            </div>
                          ))
                        }
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-zinc-500 text-sm">{t('common.loading')}</p>
              )
            ) : (
              <div className="text-center py-4">
                <Lock className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
                <p className="text-zinc-500 text-sm mb-3">
                  {t('dashboard.proOnly')}
                </p>
                <Link to="/premium">
                  <Button size="sm" className="bg-gradient-to-r from-amber to-orange-500 text-black font-semibold">
                    <Crown className="w-4 h-4 mr-1" />
                    {t('dashboard.upgradeProCta')}
                  </Button>
                </Link>
              </div>
            )}
          </motion.div>

          {/* Quick Access Grid */}
          {quickLinks.map((link, index) => (
            <motion.div
              key={link.path}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 + index * 0.05 }}
            >
              <Link to={link.path}>
                <div className="bg-[#121212] border border-white/5 rounded-2xl p-6 hover:border-amber/30 transition-all group cursor-pointer h-full">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${link.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <link.icon className="w-6 h-6 text-black" />
                  </div>
                  <h3 className="font-heading font-bold text-white">{link.label}</h3>
                  {stats && link.statKey && (
                    <p className="text-zinc-500 text-sm mt-1">
                      {stats[link.statKey]} {link.statLabel}
                    </p>
                  )}
                  {link.description && (
                    <p className="text-zinc-500 text-sm mt-1">{link.description}</p>
                  )}
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
